// The people who live in the building (world.json `cast`), turned into actors for one moment in time. Everything is a
// pure function of the clock and the person's id, so there is no state to keep: the same `now` always gives the same
// frame, a person's rhythm is their own, and a reload picks up mid-stride.
import type { CastEntry, FloorId, Waypoint, WorldMeta } from './assets';
import type { Actor, DeskPose, Face, Look } from './scene';

export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Where this moment falls in a person's own repeating cycle: [0, 1). */
function phase(now: number, h: number, period: number): number {
  return (((now + (h % 100003)) % period) + period) % period / period;
}

/** Breathing: the upper body settles one pixel for a little under half of each breath (3.2 to 4.6 s). */
export function breath(now: number, h: number): number {
  return phase(now, h, 3200 + (h % 1400)) < 0.42 ? 1 : 0;
}

/** Glances: mostly ahead, now and then to one side for a moment, sometimes the other side. */
export function glance(now: number, h: number): Look {
  const p = phase(now, h >>> 3, 5200 + (h % 6000));
  if (p < 0.62) return 0;
  if (p < 0.8) return h % 2 ? 1 : -1;
  if (p < 0.88) return 0;
  return h % 2 ? -1 : 1;
}

/** Two people in conversation take turns: returns whether `id` is the one speaking now. */
export function speaking(now: number, id: string, partner: string | undefined): boolean {
  if (!partner) return phase(now, hashStr(id), 6000) < 0.45;
  const pair = id < partner ? `${id}|${partner}` : `${partner}|${id}`;
  const ph = hashStr(pair);
  const turn = 2400 + (ph % 1800);
  const t = now + (ph % 50000);
  const who = Math.floor(t / turn) % 2;
  const within = (t % turn) / turn;
  // a beat of silence between turns
  return within > 0.12 && within < 0.92 && who === (id < partner ? 0 : 1);
}

export interface WalkState { x: number; y: number; view: 'front' | 'back' | 'l' | 'r'; step: number; hidden: boolean; moving: boolean }

const VIEWS = new Set(['front', 'back', 'l', 'r']);

function heading(dx: number, dy: number): WalkState['view'] {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'r' : 'l';
  return dy > 0 ? 'front' : 'back';
}

/** Position on a looping path: pause at each waypoint, walk to the next at `speed` art px per second, back to the start. */
export function walkAt(path: Waypoint[], speed: number, now: number, h: number): WalkState {
  const n = path.length;
  if (n === 0) return { x: 0, y: 0, view: 'front', step: 0, hidden: true, moving: false };
  const p0 = path[0];
  if (n === 1) return { x: p0[0], y: p0[1], view: VIEWS.has(p0[3] ?? '') ? (p0[3] as WalkState['view']) : 'front', step: 0, hidden: p0[3] === 'door', moving: false };
  const v = Math.max(1, speed) / 1000;
  const legs = path.map((p, i) => {
    const q = path[(i + 1) % n];
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
    return { p, q, len, pause: Math.max(0, p[2] ?? 0), dur: len / v };
  });
  const total = legs.reduce((s, l) => s + l.pause + l.dur, 0);
  let t = (((now + (h % 600011)) % total) + total) % total;
  for (let i = 0; i < n; i += 1) {
    const l = legs[i];
    const arrived = legs[(i + n - 1) % n];
    if (t < l.pause) {
      const hold = l.p[3];
      const view = hold && VIEWS.has(hold) ? (hold as WalkState['view']) : heading(arrived.q[0] - arrived.p[0], arrived.q[1] - arrived.p[1]);
      return { x: l.p[0], y: l.p[1], view, step: 0, hidden: hold === 'door', moving: false };
    }
    t -= l.pause;
    if (t < l.dur) {
      const k = l.dur > 0 ? t / l.dur : 1;
      const dx = l.q[0] - l.p[0];
      const dy = l.q[1] - l.p[1];
      const d = k * l.len;
      const view = heading(dx, dy);
      const step = view === 'l' || view === 'r' ? Math.floor(d / 3) % 4 : Math.floor(d / 5) % 2;
      return { x: Math.round(l.p[0] + dx * k), y: Math.round(l.p[1] + dy * k), view, step, hidden: false, moving: true };
    }
    t -= l.dur;
  }
  return { x: p0[0], y: p0[1], view: 'front', step: 0, hidden: false, moving: false };
}

/** What a scene may change about one cast member for as long as it is up; null hides the person. */
export type CastOverride = Record<string, null | {
  pose?: string; face?: Face; talking?: boolean; look?: Look; view?: 'front' | 'back' | 'l' | 'r'; arms?: string;
}>;

/** A desk pose the light atlases carry. */
const DESK: ReadonlySet<string> = new Set(['folded', 'write', 'read', 'gesture', 'chin', 'think']);

function deskPose(e: CastEntry, now: number, h: number, talking: boolean, animate: boolean): DeskPose {
  const base = (DESK.has(e.pose ?? '') ? e.pose : 'folded') as DeskPose;
  if (!animate) return base;
  const life = e.life;
  // a reader looks up from the page now and then; a writer stops to think
  if (base === 'read' && life.includes('read') && phase(now, h >>> 5, 7000 + (h % 4000)) > 0.84) return 'folded';
  if (base === 'write' && life.includes('write') && phase(now, h >>> 5, 9000 + (h % 5000)) > 0.88) return 'think';
  if (talking && life.includes('gesture') && phase(now, h >>> 7, 1800) < 0.5) return 'gesture';
  return base;
}

/** The forearms shift a pixel while a pen scratches (bursts of writing). */
function scratch(now: number, h: number): number {
  const burst = phase(now, h >>> 9, 3600 + (h % 1500)) < 0.55;
  return burst ? Math.floor((now + h) / 110) % 2 : 0;
}

/**
 * The cast on one floor as actors for this moment. `animate` false (reduced motion) gives everyone their rest pose and
 * walkers stand at their first waypoint. `skipRooms` drops the cast of rooms whose route draws its own people there.
 */
export function castActors(m: WorldMeta, floor: FloorId, now: number, animate: boolean, skipRooms?: ReadonlySet<string>,
  overrides?: CastOverride): Actor[] {
  const out: Actor[] = [];
  for (const e0 of m.cast ?? []) {
    if (e0.floor !== floor || skipRooms?.has(e0.room)) continue;
    const ov = overrides?.[e0.id];
    if (ov === null) continue;
    const e: CastEntry = ov ? { ...e0, ...(ov.pose ? { pose: ov.pose } : {}), ...(ov.view ? { view: ov.view } : {}),
      ...(ov.face ? { face: ov.face } : {}), ...(ov.look !== undefined ? { look: ov.look } : {}), ...(ov.arms ? { arms: ov.arms } : {}),
      ...(ov.talking !== undefined ? { life: ov.talking ? [...e0.life.filter((l) => l !== 'talk'), 'talk'] : e0.life.filter((l) => l !== 'talk'), with: ov.talking ? undefined : e0.with } : {}) } : e0;
    const h = hashStr(e.id);
    const life = e.life ?? [];
    const bob = animate && life.includes('breathe') ? breath(now, h) : 0;
    const talking = animate && life.includes('talk') ? speaking(now, e.id, e.with) : false;
    const look: Look = e.look !== undefined ? (e.look as Look) : animate && life.includes('look') ? glance(now, h) : 0;
    const base = { person: e.person, face: e.face as Face | undefined, look, talking };
    switch (e.kind) {
      case 'desk':
        out.push({ ...base, kind: 'seat', x: e.x, y: e.y, pose: deskPose(e, now, h, talking, animate), bob,
          armDx: animate && life.includes('write') && e.pose === 'write' ? scratch(now, h) : 0 });
        break;
      case 'back':
        out.push({ ...base, kind: 'back', x: e.x, y: e.y, bob });
        break;
      case 'side':
        out.push({ ...base, kind: 'side', x: e.x, y: e.y, facing: e.facing ?? 'r', bob });
        break;
      case 'stand': {
        let view = e.view ?? 'front';
        if (animate && life.includes('turn') && e.views?.length) {
          view = e.views[Math.floor((now + h) / (5200 + (h % 4000))) % e.views.length];
        }
        const shift = animate && life.includes('shift') && phase(now, h >>> 11, 5600 + (h % 3000)) > 0.6 ? (h % 2 ? 1 : -1) : 0;
        const arms = e.arms ?? (talking && life.includes('point') ? (h % 2 ? 'point' : 'point_l') : undefined);
        out.push({ ...base, kind: 'stand', x: e.x + shift, y: e.y, view, bob, clip: e.clip, arms });
        break;
      }
      case 'walk': {
        if (!e.path?.length) break;
        const w = animate ? walkAt(e.path, e.speed ?? 18, now, h) : walkAt([e.path[0]], 0, 0, 0);
        if (w.hidden) break;
        out.push({ ...base, kind: 'stand', x: w.x, y: w.y, view: w.view, step: w.moving ? w.step : undefined,
          bob: w.moving ? 0 : bob, clip: e.clip, look: w.moving ? 0 : look, talking: w.moving ? false : talking, arms: e.arms });
        break;
      }
      default:
        break;
    }
  }
  return out;
}

/** Atlas files the cast on a floor needs (to preload with the floor). */
export function castPeople(m: WorldMeta, floor: FloorId): string[] {
  return [...new Set((m.cast ?? []).filter((e) => e.floor === floor).map((e) => e.person.replace('@', '-')))];
}
